import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { qaHref } from '../contextLinks';

// SQA-20: review/promotion queue for Plaud-extracted action-item CANDIDATES.
//
// HARD RULE: extraction stages candidates; a candidate becomes a real
// sales_qa.action_item ONLY when a human promotes it here. Side-by-side:
// transcript excerpt (left of the editor) + candidate editor (right). Keyboard:
// A approve · E edit · R reject · D duplicate · P promote · j/k move.

const ITEM_KINDS = [
  'action_item',
  'promise',
  'decision',
  'open_question',
  'project_brief_fact',
  'ambiguity',
] as const;

// Default reviewer routing surfaces here as owner options (roster mirrors
// src/users.ts). Erik = sales/customer; Vince = production/handoff/vendor.
const OWNERS: Array<{ email: string; label: string }> = [
  { email: 'erik.carver@overlanddesignbuild.com', label: 'Erik — sales/customer' },
  { email: 'vince.e@overlanddesignbuild.com', label: 'Vince — production/handoff' },
  { email: 'nate.r@overlanddesignbuild.com', label: 'Nate — production' },
  { email: 'randy.b@overlanddesignbuild.com', label: 'Randy — technician' },
  { email: 'ron.s@overlanddesignbuild.com', label: 'Ron — customer success' },
];

interface Item {
  id: string;
  plaud_transcript_id: string | null;
  item_kind: string;
  description: string;
  notes: string | null;
  suggested_owner_email: string | null;
  suggested_due_date: string | null;
  suggested_contact_id: string | null;
  customer_owned: boolean | null;
  customer_owned_type: string | null;
  confidence: number | null;
  reason: string | null;
  source_quote: string | null;
  start_ms: number | null;
  end_ms: number | null;
  speaker: string | null;
  review_status: string;
  contact_name: string | null;
}

interface Group {
  transcript_id: string | null;
  meeting_title: string | null;
  meeting_date: string | null;
  items: Item[];
}

type Banner = { kind: 'ok' | 'err'; text: string } | null;

function formatDate(value: string | null): string {
  if (!value) return 'No date';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPercent(value: number | null): string {
  if (value == null) return 'No score';
  return `${Math.round(value * 100)}%`;
}

function kindLabel(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function ownerLabel(email: string): string {
  return OWNERS.find((owner) => owner.email === email)?.label ?? email;
}

export function PlaudReview(): JSX.Element {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Editable fields for the selected candidate (promote applies these).
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [itemKind, setItemKind] = useState<string>('action_item');

  const editing = useRef(false);
  const descRef = useRef<HTMLTextAreaElement>(null);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const current = useMemo(() => flat.find((i) => i.id === selectedId) ?? null, [flat, selectedId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/plaud/review-queue?status=pending&limit=300', {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as { groups: Group[]; total: number };
      setGroups(data.groups);
      setSelectedId(data.groups[0]?.items[0]?.id ?? null);
    } catch (err) {
      setBanner({ kind: 'err', text: `Failed to load: ${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Sync editable fields when selection changes.
  useEffect(() => {
    if (!current) return;
    setDescription(current.description);
    setNotes(current.notes ?? '');
    setOwnerEmail(current.suggested_owner_email ?? OWNERS[0]!.email);
    setDueDate(current.suggested_due_date ?? '');
    setItemKind(current.item_kind ?? 'action_item');
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop an actioned item from the queue, keep the selection sensible.
  const dropItem = useCallback(
    (id: string) => {
      setGroups((prev) =>
        prev
          .map((g) => ({ ...g, items: g.items.filter((i) => i.id !== id) }))
          .filter((g) => g.items.length > 0),
      );
    },
    [],
  );

  useEffect(() => {
    // After a drop, if the selected item is gone, pick the next available one.
    if (selectedId && !flat.some((i) => i.id === selectedId)) {
      setSelectedId(flat[0]?.id ?? null);
    }
  }, [flat, selectedId]);

  const post = useCallback(
    async (id: string, path: string, body: Record<string, unknown>): Promise<boolean> => {
      const res = await fetch(`/api/plaud/review-items/${id}/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
      return true;
    },
    [],
  );

  const act = useCallback(
    async (path: string, body: Record<string, unknown>, okMsg: string) => {
      if (!current || busy) return;
      setBusy(true);
      try {
        await post(current.id, path, body);
        setBanner({ kind: 'ok', text: okMsg });
        dropItem(current.id);
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [current, busy, post, dropItem],
  );

  const promote = useCallback(
    () =>
      act(
        'promote',
        { description, notes: notes || null, owner_email: ownerEmail, due_date: dueDate || null },
        'Promoted to a real action item.',
      ),
    [act, description, notes, ownerEmail, dueDate],
  );

  const approve = useCallback(() => act('approve', {}, 'Approved.'), [act]);
  const reject = useCallback(() => {
    const note = window.prompt('Reason for rejecting? (optional)') ?? undefined;
    void act('reject', { note }, 'Rejected (kept for audit).');
  }, [act]);
  const markDuplicate = useCallback(() => {
    const dup = window.prompt('Duplicate of which item id? (optional)');
    void act('duplicate', { duplicate_of_item_id: dup ? Number(dup) : undefined }, 'Marked duplicate.');
  }, [act]);
  const needsContext = useCallback(() => {
    const note = window.prompt('What context is missing? (optional)') ?? undefined;
    void act('needs-context', { note }, 'Flagged: needs more context.');
  }, [act]);

  // Save edits without promoting (parks the item as 'edited').
  const saveEdit = useCallback(
    () =>
      act(
        'edit',
        { description, notes: notes || null, owner_email: ownerEmail, due_date: dueDate || null, item_kind: itemKind },
        'Edits saved (item marked edited).',
      ),
    [act, description, notes, ownerEmail, dueDate, itemKind],
  );

  // Batch-promote every pending item in one meeting.
  const promoteGroup = useCallback(
    async (g: Group) => {
      if (busy || g.items.length === 0) return;
      setBusy(true);
      try {
        const ids = g.items.map((i) => Number(i.id));
        const res = await fetch('/api/plaud/review-items/batch-promote', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids }),
        });
        const payload = (await res.json().catch(() => ({}))) as { promoted_count?: number; error?: string };
        if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
        setBanner({ kind: 'ok', text: `Promoted ${payload.promoted_count ?? 0} item(s) from this meeting.` });
        for (const i of g.items) dropItem(i.id);
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [busy, dropItem],
  );

  const move = useCallback(
    (delta: number) => {
      if (flat.length === 0) return;
      const idx = Math.max(0, flat.findIndex((i) => i.id === selectedId));
      const next = Math.min(Math.max(idx + delta, 0), flat.length - 1);
      setSelectedId(flat[next]!.id);
    },
    [flat, selectedId],
  );

  // Keyboard shortcuts (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing.current) return;
      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          move(1);
          break;
        case 'k':
        case 'ArrowUp':
          move(-1);
          break;
        case 'a':
          void approve();
          break;
        case 'e':
          e.preventDefault();
          descRef.current?.focus();
          break;
        case 'r':
          reject();
          break;
        case 'd':
          markDuplicate();
          break;
        case 'p':
          void promote();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move, approve, reject, markDuplicate, promote]);

  const metrics = useMemo(() => {
    return {
      pending: flat.length,
      meetings: groups.length,
      customerOwned: flat.filter((item) => item.customer_owned === true).length,
      selectedKind: current ? kindLabel(current.item_kind) : 'None',
    };
  }, [current, flat, groups.length]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-7xl">
      <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Review work
          </p>
          <h1 className="text-2xl font-semibold text-slate-950">Plaud transcript review</h1>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Review transcript-mined work before it becomes operational truth. The
            left side keeps each meeting together; the right side is the human
            promotion decision.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto"
        >
          Refresh
        </button>
      </header>

      <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Pending</div>
          <div className="mt-1 text-2xl font-semibold text-slate-950">
            {loading ? '—' : metrics.pending}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Meetings</div>
          <div className="mt-1 text-2xl font-semibold text-slate-950">
            {loading ? '—' : metrics.meetings}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Customer-owned</div>
          <div className="mt-1 text-2xl font-semibold text-amber-700">
            {loading ? '—' : metrics.customerOwned}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <div className="text-xs font-medium text-slate-500">Selected kind</div>
          <div className="mt-2 truncate text-sm font-semibold text-slate-950">
            {loading ? '—' : metrics.selectedKind}
          </div>
        </div>
      </section>

      {banner && (
        <div
          className={`mb-3 rounded-md px-3 py-2 text-sm ${
            banner.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
              : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
          }`}
        >
          {banner.text}
        </div>
      )}

      {!loading && flat.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-slate-500 ring-1 ring-slate-200">
          Queue is clear — no pending Plaud candidates. New transcript uploads stage here for review
          before anything reaches the real action-item list.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[24rem_minmax(0,1fr)]">
          <section className="max-h-[72vh] space-y-3 overflow-auto">
            {groups.map((g) => (
              <div
                key={g.transcript_id ?? 'unattributed'}
                className="rounded-lg bg-white ring-1 ring-slate-200"
              >
                <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-semibold text-slate-950">
                      {g.meeting_title ?? (g.transcript_id ? `Transcript #${g.transcript_id}` : 'Unattributed')}
                    </h2>
                    <div className="text-xs text-slate-400">
                      {formatDate(g.meeting_date)} · {g.items.length} pending
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void promoteGroup(g)}
                    title="Promote every pending item in this meeting"
                    className="shrink-0 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Promote all
                  </button>
                </div>
                <ul className="divide-y divide-slate-100">
                  {g.items.map((it) => (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(it.id)}
                        className={`w-full px-4 py-3 text-left text-sm ${
                          it.id === selectedId
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className="line-clamp-2 font-medium">{it.description}</div>
                        <div
                          className={`mt-1 flex flex-wrap gap-2 text-xs ${
                            it.id === selectedId ? 'text-slate-300' : 'text-slate-400'
                          }`}
                        >
                          <span>{kindLabel(it.item_kind)}</span>
                          <span>{formatPercent(it.confidence)}</span>
                          <span>
                          {it.customer_owned === true ? 'customer-owned' : it.customer_owned === false ? 'internal' : 'unclassified'}
                            {it.customer_owned_type ? ` · ${it.customer_owned_type}` : ''}
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          {current && (
            <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-950">
                    Promote or classify transcript evidence
                  </h2>
                  <p className="text-xs text-slate-500">
                    This is still staging until you promote it.
                  </p>
                </div>
                <Link
                  to={qaHref(current.description)}
                  className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                >
                  Ask evidence
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="space-y-4">
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500">Promoted follow-up</span>
                    <textarea
                      ref={descRef}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onFocus={() => (editing.current = true)}
                      onBlur={() => (editing.current = false)}
                      rows={4}
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500">Reviewer notes</span>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      onFocus={() => (editing.current = true)}
                      onBlur={() => (editing.current = false)}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900"
                    />
                  </label>

                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="text-sm">
                      <span className="block text-xs font-medium text-slate-500">Owner</span>
                      <select
                        value={ownerEmail}
                        onChange={(e) => setOwnerEmail(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                      >
                        {OWNERS.map((o) => (
                          <option key={o.email} value={o.email}>
                            {ownerLabel(o.email)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="block text-xs font-medium text-slate-500">Due date</span>
                      <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        onFocus={() => (editing.current = true)}
                        onBlur={() => (editing.current = false)}
                        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="block text-xs font-medium text-slate-500">Kind</span>
                      <select
                        value={itemKind}
                        onChange={(e) => setItemKind(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
                      >
                        {ITEM_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {kindLabel(k)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void promote()}
                      className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Promote
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void approve()}
                      className="rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void saveEdit()}
                      className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Save edits
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={markDuplicate}
                      className="rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={needsContext}
                      className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Needs context
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={reject}
                      className="rounded-md border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>

                <aside className="space-y-3">
                  <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700 ring-1 ring-slate-200">
                    <div className="mb-1 text-xs font-medium text-slate-500">
                      Transcript excerpt
                    </div>
                  {current.source_quote ? (
                    <p className="italic whitespace-pre-wrap">“{current.source_quote}”</p>
                  ) : (
                    <p className="text-slate-400">No excerpt captured.</p>
                  )}
                  <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                    <div>
                      Source:{' '}
                      {current.plaud_transcript_id ? `transcript #${current.plaud_transcript_id}` : '—'}
                      {current.start_ms != null || current.end_ms != null
                        ? ` [${current.start_ms ?? '?'}–${current.end_ms ?? '?'}ms]`
                        : ''}
                    </div>
                    {current.speaker && <div>Speaker: {current.speaker}</div>}
                    {current.contact_name && <div>Contact: {current.contact_name}</div>}
                    {current.reason && (
                      <div>
                        <span className="font-medium">Why:</span> {current.reason}
                      </div>
                    )}
                    </div>
                  </div>
                  <details className="rounded-md border border-slate-200 bg-white p-3">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                      Keyboard shortcuts
                    </summary>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500">
                      <span>J / K: Move</span>
                      <span>A: Approve</span>
                      <span>E: Edit</span>
                      <span>P: Promote</span>
                      <span>R: Reject</span>
                      <span>D: Duplicate</span>
                    </div>
                  </details>
                </aside>
              </div>
            </section>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
